import { useCallback, useMemo } from 'react';
import {
  useWithdrawBankTransfer,
  useWithdrawBankTransferModals,
  type WithdrawBankTransferBodyRequest,
  type WithdrawBankTransferErrorResponse,
  type WithdrawBankTransferState,
} from '@/features/withdraw-bank-transfer';
import { useTranslation } from 'react-i18next';
import type {
  TwoFAParams,
  WithdrawCommissionData,
  WithdrawSuccessResponse,
} from '@/shared/types';
import type { InfoDataGridData } from '@/shared/components';
import { WithdrawConfirmationModal } from '@/shared/components';
import { useWithdrawMFAFlow } from '@/shared/lib';
import {
  getCalculatedCommission,
  useAfterOperationRefresh,
} from '@/entities/operation';

interface WithdrawConfirmationModalContainerProps {
  onBack: () => void;
  storeData: WithdrawBankTransferState;
  commissionData: WithdrawCommissionData;
  continueMFA?: boolean;
}

export const WithdrawConfirmationModalContainer = ({
  onBack,
  storeData,
  commissionData,
  continueMFA,
}: WithdrawConfirmationModalContainerProps) => {
  const { t } = useTranslation();
  const { openResultModal, openConfirmationModal } =
    useWithdrawBankTransferModals();

  const afterOperationRefresh = useAfterOperationRefresh();

  const lastStep = useMemo(() => {
    return storeData?.formStepsData && storeData.formStepsData.length > 0
      ? storeData.formStepsData[storeData.formStepsData.length - 1]
      : null;
  }, [storeData?.formStepsData]);

  const initialParams: WithdrawBankTransferBodyRequest = useMemo(() => {
    const currencyCode =
      lastStep?.amountCurrency || storeData?.selectedCurrency?.code || '';
    const amount = storeData?.totalAmount || '0';
    const amountNumber =
      typeof amount === 'string' ? parseFloat(amount) : amount;

    const totalAmount = getCalculatedCommission({
      amount: amountNumber,
      includeFee: commissionData.includeFee,
      fee: commissionData.fee,
    });

    const params =
      lastStep?.params
        ?.map(param => {
          const paramValue = lastStep.values?.[param.paramId];
          if (!paramValue || paramValue === '') {
            return null;
          }

          let value = String(paramValue);

          if (
            param.paramId === 'corrBankAccountNumber' ||
            param.paramId === 'beneficiaryAccountNumber'
          ) {
            value = value.trim().replace(/\s+/g, '');
          }

          return {
            paramId: param.paramId,
            paramName: param.paramName,
            paramValue: value,
          };
        })
        .filter(
          (
            p
          ): p is { paramId: string; paramName: string; paramValue: string } =>
            p !== null
        ) || [];

    return {
      accountNumber: storeData.accountNumber,
      amount: totalAmount.totalWithdrawAmount,
      currencyCode,
      params,
    };
  }, [storeData, lastStep]);

  const injectMFA = useCallback(
    (
      params: WithdrawBankTransferBodyRequest,
      mfa: TwoFAParams
    ): WithdrawBankTransferBodyRequest => {
      return {
        ...params,
        twoFA: mfa,
      };
    },
    []
  );

  const { showMFA, startMFAVerification, renderMFAView } = useWithdrawMFAFlow<
    WithdrawBankTransferBodyRequest,
    WithdrawSuccessResponse,
    WithdrawBankTransferErrorResponse
  >({
    initialParams,
    injectMFA,
    useMutationHook: useWithdrawBankTransfer,
    onSuccess: () => {
      openResultModal({ success: true });
      afterOperationRefresh();
    },
    onError: () => {
      openResultModal({ success: false });
    },
    onBack: openConfirmationModal,
    onClose: openConfirmationModal,
    onCancel: openConfirmationModal,
    onMFASetupComplete: () => {
      openConfirmationModal({ continueMFA: true });
    },
    modalTitle: 'withdrawMethods.crypto.confirmModal.title',
    continueMFA,
  });

  const onConfirmed = useCallback(() => {
    startMFAVerification();
  }, [startMFAVerification]);

  const currencyCode =
    lastStep?.amountCurrency || storeData?.selectedCurrency?.code || '';

  const addressInfo: InfoDataGridData = storeData
    ? [
        {
          key: t('withdrawMethods.bankTransfer.currency'),
          value: currencyCode,
        },
        {
          key: t('withdrawMethods.bankTransfer.accountNumber'),
          value:
            storeData.withdrawBankTransferFormData.beneficiaryAccountNumber ||
            '',
        },
        {
          key: t('withdrawMethods.bankTransfer.bic'),
          value: storeData.withdrawBankTransferFormData.bankCode || '',
        },
        {
          key: t('withdrawMethods.bankTransfer.bankName'),
          value: storeData.withdrawBankTransferFormData.bankName || '',
        },
      ]
    : [];

  const amountsInfo: InfoDataGridData =
    storeData && commissionData
      ? [
          {
            key: t('withdrawMethods.amountInfo.totalWithdraw'),
            value: commissionData.totalWithdrawAmountStr,
          },
          {
            key: t('withdrawMethods.amountInfo.withdrawFee'),
            value: commissionData.feeStr,
          },
          {
            key: t('withdrawMethods.amountInfo.toBeReceived'),
            value: commissionData.toBeReceivedStr,
          },
        ]
      : [];

  if (showMFA) {
    return renderMFAView();
  }

  return (
    <WithdrawConfirmationModal
      title={t('withdrawMethods.crypto.confirmModal.title')}
      onBack={onBack}
      infoData={[addressInfo, amountsInfo]}
      onConfirmed={onConfirmed}
      isLoading={false}
      buttonText={t('withdrawMethods.withdrawButton')}
    />
  );
};
